import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { noPaginationParams } from '@/common/constants'
import { transformResponseReturnInstruction } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/ReturnInstructions/ReturnInstructionDetailConfiguration/hooks/useReturnInstructionSet/transformers'
import configurationServices from '@/configuration/services/services'
import { transformResponseLogisticsPreferences } from '@/configuration/transformers/logisticsPreferences/transformers'

import { transformResponseReturnInstructions } from '../useReturnInstructionSets/transformers'

const useDeleteReturnInstructionSet = ({ storeId, refetch, setToastInfo }) => {
  const { t } = useTranslation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [instructionToDelete, setInstructionToDelete] = useState(null)
  const [associatedDeliveryMethods, setAssociatedDeliveryMethods] = useState([])
  const [replacementId, setReplacementId] = useState(null)
  const [availableReplacements, setAvailableReplacements] = useState([])
  const [loadingModalData, setLoadingModalData] = useState(false)

  const { fetchData: fetchLogisticsPreferences } = useFetch(configurationServices.getStoreLogisticsPreferences, {
    doInitialRequest: false,
    transformResponse: transformResponseLogisticsPreferences,
  })

  const { fetchData: fetchDetail } = useFetch(configurationServices.getReturnInstructionSet, {
    doInitialRequest: false,
    transformResponse: transformResponseReturnInstruction,
  })

  const { fetchData: fetchAllSets } = useFetch(configurationServices.getReturnInstructionSets, {
    doInitialRequest: false,
    transformResponse: transformResponseReturnInstructions,
  })

  const { fetchData: deleteRequest, loading: deletingInstruction } = useFetch(
    configurationServices.deleteReturnInstructionSet,
    { doInitialRequest: false }
  )

  const openDeleteModal = async instruction => {
    setInstructionToDelete(instruction)
    setReplacementId(null)
    setAssociatedDeliveryMethods([])
    setAvailableReplacements([])
    setShowDeleteModal(true)
    setLoadingModalData(true)

    try {
      const detail = await fetchDetail({ storeId, returnInstructionSetId: instruction.id })

      if (detail?.delivery_method_ids?.length) {
        const logistics = await fetchLogisticsPreferences({ storeId })
        const resolvedMethods =
          logistics?.delivery_methods?.filter(dm => detail.delivery_method_ids.includes(dm.id)) || []

        setAssociatedDeliveryMethods(resolvedMethods)
        const allSets = await fetchAllSets({ storeId, ...noPaginationParams })
        const sameTypeReplacements = (allSets?.results || []).filter(
          ris => ris.id !== instruction.id && ris.type === instruction.type
        )

        setAvailableReplacements(sameTypeReplacements)
      }
    } catch (error) {
      setShowDeleteModal(false)
      setInstructionToDelete(null)
      setToastInfo({ show: true, message: error?.errorMessage, type: 'error' })
    } finally {
      setLoadingModalData(false)
    }
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false)
    setInstructionToDelete(null)
    setAssociatedDeliveryMethods([])
    setReplacementId(null)
    setAvailableReplacements([])
  }

  const isFormValid = () => {
    if (!associatedDeliveryMethods.length) return true

    return !!replacementId
  }

  const deleteInstruction = async () => {
    try {
      await deleteRequest({
        storeId,
        returnInstructionSetId: instructionToDelete.id,
        replacementId: associatedDeliveryMethods.length ? replacementId : undefined,
      })

      closeDeleteModal()
      refetch()
      setToastInfo({ show: true, message: t('Instrucciones de devolución eliminadas'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    showDeleteModal,
    instructionToDelete,
    openDeleteModal,
    closeDeleteModal,
    associatedDeliveryMethods,
    replacementId,
    setReplacementId,
    isFormValid,
    deleteInstruction,
    deletingInstruction,
    availableReplacements,
    loadingModalData,
  }
}

export default useDeleteReturnInstructionSet
